import { TableCell, TableRow } from '@heroui/react';
import React from 'react';
import { FiUsers } from 'react-icons/fi';

import {
  LoadingTable,
  type LoadingTableColumn,
} from '../../../../../../../components/Table';
import { type CustomerTaxProfileRow } from '../types';
import { formatProfileUpdatedAt } from '../utils/chatTaxProfileMapper';
import { ABSENT_VALUE } from '../utils/taxJurisdictionMapper';

interface CustomerTaxProfileTableProps {
  customers: CustomerTaxProfileRow[];
  isLoading: boolean;
}

const columns: LoadingTableColumn[] = [
  { label: 'Customer', className: 'pl-6 min-w-48' },
  { label: 'Chat id', className: 'hidden lg:table-cell min-w-40' },
  { label: 'Jurisdiction', className: 'min-w-28' },
  { label: 'Country', className: 'hidden md:table-cell min-w-24' },
  { label: 'Rates', className: 'min-w-20' },
  { label: 'Updated', className: 'hidden md:table-cell min-w-40' },
];

export const CustomerTaxProfileTable: React.FC<
  CustomerTaxProfileTableProps
> = ({ customers, isLoading }) => {
  const renderRow = (customer: CustomerTaxProfileRow) => (
    <TableRow key={customer.id}>
      <TableCell className="pl-6">
        <div className="flex flex-col">
          <span className="font-medium text-foreground">
            {customer.customerName}
          </span>
          {customer.customerNumber && (
            <span className="font-mono text-xs text-default-500">
              {customer.customerNumber}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <span className="font-mono text-xs text-default-600 select-text cursor-text">
          {customer.chatId}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-foreground">
          {customer.jurisdictionCode ?? ABSENT_VALUE}
        </span>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-sm text-default-600">
          {customer.countryCode ?? ABSENT_VALUE}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-default-600">
          {customer.rateCount ?? ABSENT_VALUE}
        </span>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className="text-sm text-default-600">
          {formatProfileUpdatedAt(customer.updatedAt)}
        </span>
      </TableCell>
    </TableRow>
  );

  return (
    <LoadingTable
      columns={columns}
      data={customers}
      renderRow={renderRow}
      isLoading={isLoading}
      isEmpty={customers.length === 0}
      rowCount={5}
      ariaLabel="Customer tax profiles table"
      loadingMessage="Loading customers..."
      emptyState={{
        title: 'No customers',
        description: 'No customers have a tax jurisdiction assigned.',
        icon: FiUsers,
      }}
    />
  );
};
